import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth } from '../../contexts/AuthContext'
import { 
  HomeIcon, 
  Cog6ToothIcon, 
  ArrowRightOnRectangleIcon,
  AcademicCapIcon,
  UserGroupIcon,
  UsersIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth()
  const router = useRouterState()

  const isActive = (path: string) => {
    // Exact match or starts with path for nested routes
    if (router.location.pathname === path) return true
    if (path !== '/dashboard' && router.location.pathname.startsWith(path + '/')) return true
    return false
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  
  const navItems = [
    { path: '/dashboard', label: 'Home', icon: HomeIcon },
    { path: '/dashboard/calendar', label: 'Calendar', icon: CalendarDaysIcon },
    { path: '/dashboard/customers', label: 'Customers', icon: UsersIcon },
    { path: '/dashboard/team-members', label: 'Team Members', icon: UserGroupIcon },
    ...(isAdmin ? [{ path: '/dashboard/skills-bank', label: 'Skills Bank', icon: AcademicCapIcon }] : []),
    { path: '/dashboard/skills', label: 'My Skills', icon: AcademicCapIcon },
    { path: '/dashboard/settings', label: 'Settings', icon: Cog6ToothIcon },
  ]

  const handleLogout = async () => {
    await logout()
    onClose()
  }

  return (
    <>
      {/* Mobile Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        className={`fixed lg:relative top-0 left-0 h-screen bg-base-100 border-r border-base-300 z-50 transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } w-64 flex flex-col`}
      >
        {/* Header */}
        <div className="p-4 border-b border-base-300 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg">zkejul</h2>
            <p className="text-xs text-base-content/60">{user?.email}</p>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden btn btn-ghost btn-sm btn-square"
            aria-label="Close sidebar"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-accent text-white font-medium'
                    : 'hover:bg-base-200'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        {/* Footer - Logout */}
        <div className="p-4 border-t border-base-300">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-error hover:text-error-content transition-colors w-full text-left"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  )
}
